import type { CoordinateTO, HubTO } from '@control-center/control-center-client-public-hubs-ts';
import { Coordinate as WireCoordinateSchema } from '@control-center/control-center-client-public-hubs-ts/dist/schemas';
import { degraded } from 'helper/zod';
import { z } from 'zod';

import type { HubFE } from '../hub';
import { makeSanitizeWire, mapListPerEntry, safeOptional, toGeoPosition } from './restMapping';

/**
 * REST -> domain mapping for hubs, served by
 * `@control-center/control-center-client-public-hubs-ts` (`HubsApi`):
 * `GET /api/v1/hubs` returns `HubTO[]` -> {@link hubsFromRestV1}. `V1` is the REST api version.
 */

const label = (field: string) => `hubFromRestV1.${field}`;

/** `createdAt` / `updatedAt` — camelCase, which is why the shared `sanitizeWire` is not used. */
const HUB_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeHubWire = makeSanitizeWire(HUB_DATE_KEYS);

const WireHubObjectSchema = z.object({
    id: z.string().min(1),
    name: safeOptional(z.string(), label('name')),
    address: safeOptional(z.string(), label('address')),
    active: safeOptional(z.boolean(), label('active')),
    position: safeOptional(WireCoordinateSchema, label('position')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
    radius: safeOptional(z.number(), label('radius')),
});

/**
 * Wire schema for a `HubTO`. Exported so `mocks/tests/restContract.test.ts` can assert a mock body
 * against the same validator the mapper uses, as it already does for `WireVehicleSchema`.
 */
export const WireHubSchema = z.preprocess(sanitizeHubWire, WireHubObjectSchema);

type WireCoordinate = Pick<CoordinateTO, 'latitude' | 'longitude'>;
type WireHubObject = z.infer<typeof WireHubObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type HubWireKeysExistOnSpec = {
    hub: KeysExistOn<WireHubObject, HubTO>;
    coordinate: KeysExistOn<WireCoordinate, CoordinateTO>;
};

export const hubFromRestV1 = (raw: unknown): HubFE | undefined => {
    const wire = WireHubSchema.parse(raw);
    const position = toGeoPosition(wire.position);
    if (position === undefined) {
        console.error(degraded(label('position'), 'hub dropped, no usable coordinates'), wire.position);
        return;
    }
    return {
        id: wire.id,
        name: wire.name ?? '',
        address: wire.address ?? undefined,
        active: wire.active ?? false,
        position,
    };
};

export const hubsFromRestV1 = (raw: unknown): HubFE[] => mapListPerEntry(raw, hubFromRestV1, label('list'));
